import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  Autocomplete,
  Box,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useTranslation } from "react-i18next";
import getPdApiBase from "utils/getPdApiBase";

const PDAPI_BASE_URL = getPdApiBase();
const LIST_THRESHOLD_API = `${PDAPI_BASE_URL}/qrcode/v1/bet/order-limit/list-threshold/`;
const LIST_EXPOSURE_API = `${PDAPI_BASE_URL}/qrcode/v1/bet/auth/list-exposure-v2/`;
const SET_THRESHOLD_API = `${PDAPI_BASE_URL}/qrcode/v1/bet/order-limit/set-threshold/`;
const POS_LIST_API = `${PDAPI_BASE_URL}/qrcode/v1/bet/auth/pos-list/`;

const numberFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 2,
});

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return numberFormatter.format(num);
};

const normalizeArray = (value) => (Array.isArray(value) ? value : []);

const collectLeafPosPids = (node, acc = []) => {
  if (!node) return acc;
  const children = normalizeArray(node.agent_result);
  if (!children.length && node.agent) {
    acc.push(String(node.agent));
    return acc;
  }
  children.forEach((child) => collectLeafPosPids(child, acc));
  return acc;
};

const buildAgentMaps = (nodes = []) => {
  const agentOptions = [];
  const agentToPos = new Map();

  const traverse = (node) => {
    if (!node) return;
    const children = normalizeArray(node.agent_result);
    if (children.length) {
      const label = String(node.agent || "").trim();
      if (label) {
        agentOptions.push(label);
        const leafPos = collectLeafPosPids(node);
        agentToPos.set(label, Array.from(new Set(leafPos)));
      }
      children.forEach(traverse);
    }
  };

  nodes.forEach(traverse);
  return { agentOptions, agentToPos };
};

const flattenAgentRows = (nodes = [], level = 0, rows = []) => {
  nodes.forEach((node) => {
    const children = normalizeArray(node.agent_result);
    rows.push({
      id: `${level}-${rows.length}-${node.agent}`,
      name: node.agent,
      exposure: node.exposure,
      level,
      isLeaf: children.length === 0,
    });
    if (children.length) {
      flattenAgentRows(children, level + 1, rows);
    }
  });
  return rows;
};

const createDialogState = (overrides = {}) => ({
  open: false,
  mode: "create",
  targetLevel: "pos",
  scope: "global",
  limitType: "stake",
  eventId: "",
  sameAmount: true,
  selectedTargets: [],
  amounts: {},
  baseAmount: "",
  ...overrides,
});

function Exposures() {
  const { t } = useTranslation("common");
  const [totalExposure, setTotalExposure] = useState("0");
  const [agentTree, setAgentTree] = useState([]);
  const [posList, setPosList] = useState([]);
  const [orderLimits, setOrderLimits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(createDialogState());
  const [dialogError, setDialogError] = useState("");
  const [infoOpen, setInfoOpen] = useState(false);
  const cardSx = {
    borderRadius: 3,
    boxShadow: "0 10px 24px rgba(0, 0, 0, 0.08)",
    border: "1px solid rgba(0, 0, 0, 0.04)",
  };

  const fetchPosList = useCallback(async () => {
    const res = await axios.get(POS_LIST_API);
    const list = normalizeArray(res.data?.pos_pids).map((pos) => String(pos));
    setPosList(list);
  }, []);

  const fetchExposure = useCallback(async () => {
    const res = await axios.get(LIST_EXPOSURE_API);
    const total = res.data?.total_exposure?.exposure ?? "0";
    const agents = normalizeArray(res.data?.agent_result);
    const fallbackPos = normalizeArray(res.data?.pos_result).map((item) => ({
      agent: item.pos_pid,
      exposure: item.exposure,
      agent_result: null,
    }));
    setTotalExposure(total);
    setAgentTree(agents.length ? agents : fallbackPos);
  }, []);

  const fetchOrderLimits = useCallback(async () => {
    const res = await axios.get(LIST_THRESHOLD_API);
    setOrderLimits(normalizeArray(res.data?.order_limit));
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([fetchPosList(), fetchExposure(), fetchOrderLimits()]);
    } catch (err) {
      console.error("Failed to load exposure data", err);
    } finally {
      setLoading(false);
    }
  }, [fetchExposure, fetchOrderLimits, fetchPosList]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const { agentOptions, agentToPos } = useMemo(() => buildAgentMaps(agentTree), [agentTree]);

  const exposureRows = useMemo(() => flattenAgentRows(agentTree), [agentTree]);

  const limitByPos = useMemo(() => {
    const map = new Map();
    orderLimits.forEach((rule) => {
      const posPid = rule?.pos_pid || rule?.posPid;
      if (!posPid || rule?.scope !== "global") return;
      if (!map.has(posPid)) {
        map.set(posPid, rule);
      }
    });
    return map;
  }, [orderLimits]);

  const openCreateDialog = () => {
    setDialog(createDialogState({ open: true }));
    setDialogError("");
  };

  const openEditDialog = (rule) => {
    const posPid = rule?.pos_pid || rule?.posPid;
    if (!posPid) return;
    setDialog(
      createDialogState({
        open: true,
        mode: "edit",
        targetLevel: "pos",
        scope: rule.scope || "global",
        limitType: rule.limit_type || "stake",
        eventId: rule.event_id || "",
        sameAmount: true,
        selectedTargets: [posPid],
        baseAmount: String(rule.limit_amount ?? ""),
      })
    );
    setDialogError("");
  };

  const closeDialog = () => {
    setDialog(createDialogState());
    setDialogError("");
  };

  const targetOptions = dialog.targetLevel === "agent" ? agentOptions : posList;

  const handleTargetsChange = (nextTargets) => {
    setDialog((prev) => {
      const nextAmounts = { ...prev.amounts };
      Object.keys(nextAmounts).forEach((key) => {
        if (!nextTargets.includes(key)) {
          delete nextAmounts[key];
        }
      });
      return { ...prev, selectedTargets: nextTargets, amounts: nextAmounts };
    });
  };

  const resolveTargetsToRequests = () => {
    if (dialog.targetLevel === "pos") {
      return dialog.selectedTargets.map((posPid) => ({
        posPid,
        amount: dialog.sameAmount ? dialog.baseAmount : dialog.amounts[posPid],
      }));
    }

    const requests = [];
    dialog.selectedTargets.forEach((agentName) => {
      const posPids = agentToPos.get(agentName) || [];
      const amount = dialog.sameAmount ? dialog.baseAmount : dialog.amounts[agentName];
      posPids.forEach((posPid) => {
        requests.push({ posPid, amount });
      });
    });

    return requests;
  };

  const handleSubmit = async () => {
    if (dialog.targetLevel === "pos" && dialog.selectedTargets.length === 0) {
      setDialogError(t("exposure.requiredPos"));
      return;
    }

    if (dialog.targetLevel === "agent" && dialog.selectedTargets.length === 0) {
      setDialogError(t("exposure.requiredAgent"));
      return;
    }

    if (dialog.scope === "match" && !dialog.eventId.trim()) {
      setDialogError(t("exposure.requiredEvent"));
      return;
    }

    if (!dialog.baseAmount && dialog.sameAmount) {
      setDialogError(t("exposure.requiredAmount"));
      return;
    }

    const requests = resolveTargetsToRequests().filter((item) => item.posPid);
    if (!requests.length) {
      setDialogError(t("exposure.requiredPos"));
      return;
    }

    const invalid = requests.find((item) => !item.amount || Number(item.amount) <= 0);
    if (invalid) {
      setDialogError(t("exposure.requiredAmount"));
      return;
    }

    try {
      await Promise.all(
        requests.map((item) =>
          axios.post(SET_THRESHOLD_API, {
            scope: dialog.scope,
            event_id: dialog.scope === "match" ? dialog.eventId.trim() : "",
            pos_pid: item.posPid,
            limit_amount: Number(item.amount),
            limit_type: dialog.limitType,
          })
        )
      );
      closeDialog();
      await fetchOrderLimits();
    } catch (err) {
      console.error("Failed to set threshold", err);
      setDialogError(t("exposure.submitFailed"));
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={5} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ overflow: "visible" }}>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
              >
                <Typography variant="h6" color="white" fontWeight={600}>
                  {t("pageTitles.exposures")}
                </Typography>
              </MDBox>
              <MDBox p={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={7}>
                    <Card sx={{ ...cardSx, height: "100%" }}>
                      <CardContent sx={{ px: 3, py: 3 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          {t("exposure.totalExposure")}
                        </Typography>
                        <Typography
                          variant="h3"
                          fontWeight={700}
                          color="textPrimary"
                          sx={{ mt: 1 }}
                        >
                          {formatNumber(totalExposure)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Card sx={{ ...cardSx, height: "100%" }}>
                      <CardContent sx={{ px: 3, py: 3 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          {t("exposure.totalLimitSettings")}
                        </Typography>
                        <MDBox
                          mt={2}
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          flexWrap="wrap"
                          gap={2}
                        >
                          <FormControlLabel
                            control={<Switch disabled />}
                            label={
                              <Typography variant="body2" fontWeight={500}>
                                {t("exposure.disableAllOrders")}
                              </Typography>
                            }
                          />
                          <Button
                            variant="contained"
                            onClick={openCreateDialog}
                            sx={{ px: 2.5, borderRadius: 2 }}
                          >
                            {t("exposure.newLimit")}
                          </Button>
                        </MDBox>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <Card sx={cardSx}>
                      <CardContent sx={{ px: 3, py: 3 }}>
                        <MDBox
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          mb={2}
                        >
                          <Typography variant="h6" fontWeight={700}>
                            {t("exposure.exposureByLevel")}
                          </Typography>
                          <Button
                            variant="text"
                            size="small"
                            startIcon={<InfoOutlinedIcon fontSize="small" />}
                            sx={{ color: "text.secondary" }}
                            onClick={() => setInfoOpen(true)}
                          >
                            {t("exposure.description")}
                          </Button>
                        </MDBox>
                        {exposureRows.length === 0 ? (
                          <Typography variant="body2" color="textSecondary">
                            {loading ? t("exposure.loading") : t("exposure.noData")}
                          </Typography>
                        ) : (
                          <Box
                            sx={{
                              borderRadius: 2,
                              border: "1px solid rgba(0, 0, 0, 0.06)",
                              overflow: "hidden",
                              backgroundColor: "#fff",
                            }}
                          >
                            <TableContainer>
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ backgroundColor: "rgba(0, 0, 0, 0.02)" }}>
                                    <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>
                                      {t("exposure.levelName")}
                                    </TableCell>
                                    <TableCell
                                      align="right"
                                      sx={{ fontWeight: 600, color: "text.secondary" }}
                                    >
                                      {t("exposure.currentExposure")}
                                    </TableCell>
                                    <TableCell
                                      align="right"
                                      sx={{ fontWeight: 600, color: "text.secondary" }}
                                    >
                                      {t("exposure.limit")}
                                    </TableCell>
                                    <TableCell
                                      align="right"
                                      sx={{ fontWeight: 600, color: "text.secondary" }}
                                    >
                                      {t("exposure.actions")}
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {exposureRows.map((row) => {
                                    const limitRule = limitByPos.get(row.name);
                                    return (
                                      <TableRow key={row.id}>
                                        <TableCell sx={{ py: 1.5 }}>
                                          <Box sx={{ pl: row.level * 3 }}>
                                            <Typography variant="body2" color="textPrimary">
                                              {row.name || "-"}
                                            </Typography>
                                          </Box>
                                        </TableCell>
                                        <TableCell align="right" sx={{ py: 1.5 }}>
                                          {formatNumber(row.exposure)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ py: 1.5 }}>
                                          {limitRule ? formatNumber(limitRule.limit_amount) : "-"}
                                        </TableCell>
                                        <TableCell align="right" sx={{ py: 1.5 }}>
                                          {row.isLeaf ? (
                                            <Button
                                              variant="text"
                                              size="small"
                                              sx={{ color: "info.main", fontWeight: 600 }}
                                              onClick={() =>
                                                limitRule
                                                  ? openEditDialog(limitRule)
                                                  : setDialog(
                                                      createDialogState({
                                                        open: true,
                                                        targetLevel: "pos",
                                                        selectedTargets: row.name ? [row.name] : [],
                                                      })
                                                    )
                                              }
                                            >
                                              {limitRule ? t("exposure.edit") : t("exposure.set")}
                                            </Button>
                                          ) : (
                                            ""
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("exposure.descriptionTitle")}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            {t("exposure.descriptionTotalTitle")}
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            {t("exposure.descriptionTotalBody")}
          </Typography>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            {t("exposure.descriptionRiskTitle")}
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            {t("exposure.descriptionRiskBody")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)}>{t("exposure.close")}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t("exposure.dialogTitle")}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <FormControl component="fieldset">
            <FormLabel>{t("exposure.targetLevelLabel")}</FormLabel>
            <RadioGroup
              row
              value={dialog.targetLevel}
              onChange={(event) =>
                setDialog((prev) => ({
                  ...prev,
                  targetLevel: event.target.value,
                  selectedTargets: [],
                  amounts: {},
                }))
              }
            >
              <FormControlLabel
                value="agent"
                control={<Radio />}
                label={t("exposure.targetLevelAgent")}
              />
              <FormControlLabel
                value="pos"
                control={<Radio />}
                label={t("exposure.targetLevelPos")}
              />
            </RadioGroup>
          </FormControl>

          <FormControl component="fieldset">
            <FormLabel>{t("exposure.scopeLabel")}</FormLabel>
            <RadioGroup
              row
              value={dialog.scope}
              onChange={(event) =>
                setDialog((prev) => ({
                  ...prev,
                  scope: event.target.value,
                  eventId: event.target.value === "match" ? prev.eventId : "",
                }))
              }
            >
              <FormControlLabel value="global" control={<Radio />} label={t("exposure.scopeAll")} />
              <FormControlLabel
                value="match"
                control={<Radio />}
                label={t("exposure.scopeMatch")}
              />
            </RadioGroup>
          </FormControl>

          {dialog.scope === "match" && (
            <TextField
              label={t("exposure.eventIdLabel")}
              value={dialog.eventId}
              onChange={(event) => setDialog((prev) => ({ ...prev, eventId: event.target.value }))}
              fullWidth
            />
          )}

          <FormControl component="fieldset">
            <FormLabel>{t("exposure.limitTypeLabel")}</FormLabel>
            <RadioGroup
              row
              value={dialog.limitType}
              onChange={(event) =>
                setDialog((prev) => ({ ...prev, limitType: event.target.value }))
              }
            >
              <FormControlLabel
                value="stake"
                control={<Radio />}
                label={t("exposure.limitTypeStake")}
              />
              <FormControlLabel
                value="exposure"
                control={<Radio />}
                label={t("exposure.limitTypeExposure")}
              />
            </RadioGroup>
          </FormControl>

          <Autocomplete
            multiple
            options={targetOptions}
            value={dialog.selectedTargets}
            disableCloseOnSelect
            onChange={(event, value) => handleTargetsChange(value)}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox checked={selected} sx={{ mr: 1 }} />
                {option}
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip label={option} {...getTagProps({ index })} key={option} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={
                  dialog.targetLevel === "agent"
                    ? t("exposure.agentSelectLabel")
                    : t("exposure.posSelectLabel")
                }
                placeholder={t("exposure.selectPlaceholder")}
              />
            )}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={dialog.sameAmount}
                onChange={(event) =>
                  setDialog((prev) => ({
                    ...prev,
                    sameAmount: event.target.checked,
                  }))
                }
              />
            }
            label={t("exposure.sameLimit")}
          />

          {dialog.sameAmount ? (
            <TextField
              type="number"
              label={t("exposure.amountLabel")}
              value={dialog.baseAmount}
              onChange={(event) =>
                setDialog((prev) => ({ ...prev, baseAmount: event.target.value }))
              }
              fullWidth
            />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {dialog.selectedTargets.map((target) => (
                <TextField
                  key={target}
                  type="number"
                  label={target}
                  value={dialog.amounts[target] || ""}
                  onChange={(event) =>
                    setDialog((prev) => ({
                      ...prev,
                      amounts: { ...prev.amounts, [target]: event.target.value },
                    }))
                  }
                  fullWidth
                />
              ))}
            </Box>
          )}

          {dialogError && (
            <Typography color="error" variant="body2">
              {dialogError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>{t("exposure.cancel")}</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {dialog.mode === "edit" ? t("exposure.save") : t("exposure.submit")}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Exposures;
